import { useEffect, useState } from 'react';
import { getSuggestedProgram, setCurrentStep, setExistingPatientData } from '@stores/onBoard/onBoard';
import { useTypedDispatch, useTypedSelector } from '@stores/index';
import { getRomSessionList } from '@stores/rom/results';
import { getPostureByPage } from '@stores/postures/postures';
import { Spin } from 'antd';
import './style.css';
import MyProgramsPatientOnboard from '@pages/PatientOnboard/MyProgramsPatientOnboard';
import TrueToFormPatientOnboard from '@pages/PatientOnboard/TrueToFormPatientOnboard';
import RecommendationDashboard from '@pages/PatientDashboard/RecommendationDashboard';
import ExistingPatientDashboard from '@pages/PatientDashboard/ExistingPatientDashboard';
import PatientDashboardBanner from '@pages/PatientDashboard/PatientDashboardBanner';
import { setActiveTab } from '@stores/aiAssistant/aiAssistant';
import SuggestedPrograms from '@pages/PatientDashboard/SuggestedPrograms';
import { ACTIVETAB, INTAKE_STEPS, PLANS } from '@stores/constants';
import { getProgramList } from '@stores/aiAssistant/program';
import { ICustomRomSession, IPaginationPluginDefaultResponse, IStrapiPagination, ISuggestedProgramItem, TSuggestedProgram } from '@stores/interfaces';
import AiAssistantCustomRomScanResult from '@pages/AiAssistantCustomRomScanResult';
import { getCustomRomSessionById, setSelectedRom } from '@stores/rom/customRom';
import { getPlansByUserId } from '@stores/settings/setings';

interface Program {
  id: string;
  userId: string;
  name: string;
  description: string;
  thumbnail: string;
  active: boolean;
  duration: number;
  durationType: 'days' | 'weeks' | string;
  startAt: string | null;
  finishAt: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  status: string;
  originType: string;
  programTemplateId: string | null;
  strapiProgramTemplateId: string | null;
  exercises: Exercise[];
}



export interface IProgramItem {
  id: string;
  name: string;
  duration: string;
  durationType: string;
  description: string;
  thumbnail: string | { url: string };
  exercises: IProgramExerciseItem[];
}

export interface IProgramExerciseItem {
  strapiExerciseId: number | { id: number } | {
    id: string;
    exercise_image: {
      url: string;
    }[];
  };
  exerciseLibraryId: string | null;
  weeklyReps: number;
  dailyReps: number;
  sets: number;
  reps: number;
  thumbnail: {
    url?: string
  }
}

interface Exercise {
  id: string;
  programId: string;
  strapiExerciseId: number;
  exerciseLibraryId: string | null;
  weeklyReps: number;
  dailyReps: number;
  sets: number;
  reps: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  order: number;
  name: string;
  description: string;
  is_bilateral: boolean;
  recommended_duration: number | null;
  publishedAt: string;
  locale: string;
  instructions: string;
  contraindications: string;
  therapist_notes: string;
  breathing: string;
  supportive_surfaces: string;
  alignment_tips: string;
  frequency: string;
  equipment_alternatives: string;
  precautions: string;
  functional_applications: string;
  function: string | null;
  orientation: string | null;
  assets: Asset[];
  video: string;
  image: string;
}

interface Asset {
  id: number;
  gender: 'male' | 'female' | string;
  sideBody: 'right' | 'left' | string;
  video: {
    data: {
      id: number;
      attributes: {
        url: string;
      };
    };
  };
  image: {
    data: {
      id: number;
      attributes: {
        url: string;
      };
    };
  };
}

export interface ISelectedProgram {
  exercises?: {
    strapiExerciseId: {
      exercise_image: {
        url: string;
      }[];
      exercise_video: {
        url: string;
      }[];
      name: string;
      description: string;
    };
    weeklyReps: string;
    dailyReps: string;
    sets: string;
    reps: string;
  }[];
  functional_goals?: {
    name: string;
  }[];
  duration?: string;
  durationType?: string;
  name?: string;
  description?: string;
  updatedAt?: string;
  thumbnail: {
    url?: string;
  };
}

export interface ISuggestedProgramDetails {
  data: TSuggestedProgram[]
  meta: {
    pagination: {
      page: number;
      pageCount: number;
      pageSize: number;
      total: number;
    };
  };
}

export default function NewPatientOnboardDashboard() {
  const dispatch = useTypedDispatch()
  const [current, setCurrent] = useState(0);
  const [isLoading, setLoading] = useState(true)
  const [isLoadingExist, setLoadingExist] = useState(true)
  const [suggestedProgramData, setSuggestedProgramData] = useState<ISuggestedProgramItem[]>([]);
  const [suggestedProgram, setSuggestedProgram] = useState<ISuggestedProgramItem[]>([]);
  const { suggestedProgramDetails, savedFunctionalGoals } = useTypedSelector((state): { suggestedProgramDetails: { data: ISuggestedProgramItem[], meta: { pagination: IStrapiPagination } } | null; savedFunctionalGoals: { functionalGoalsIds: number[] } } => ({
    suggestedProgramDetails: state.onBoard.onBoard.getSuggestedProgram,
    savedFunctionalGoals: state.onBoard.onBoard.savedFunctionalGoals,
  }));
  const existingPatientData = useTypedSelector((state) => state.onBoard.onBoard.existingPatientData)
  const [currentPage, setCurrentPage] = useState<number>(1);
  const user = useTypedSelector((state) => state.user)
  const { selectedUser } = useTypedSelector(state => state.contacts.main);
  const [programData, setProgramData] = useState<Program[]>([]);
  const [pagination, setPagination] = useState<IPaginationPluginDefaultResponse | null>(null);
  const [functionalGoal, setFunctionalGoal] = useState<number[]>([])
  const [isFunctionGoalChanged, setIsFunctionGoalChanged] = useState<boolean>(false)
  const [total, setTotal] = useState<number>(1)
  const id = user.isPhysioterapist ? selectedUser?.id : user?.id;
  const [customRomSessionExercise, setCustomRomSessionExercise] = useState<ICustomRomSession[]>([]);
  const savedUserPlans = useTypedSelector((state) => state.settings.settings.savedUserPlans);
  const planType = savedUserPlans?.planType;
  const isPlanOne = planType === PLANS?.SCREENING;
  const isVirtualPt = planType === PLANS?.VIRTUALPT;
  const [isRomDataLoading, setRomDataLoading] = useState(true);

  useEffect(() => {
    if (isPlanOne) {
      fetchData(1);
    }
  }, [isPlanOne]);

  useEffect(() => {
    if (isVirtualPt) {
      fetchHomeData(1);
    }
  }, [isVirtualPt]);


  const fetchData = async (page: number) => {
    setRomDataLoading(true);
    try {
      const data = await dispatch(getRomSessionList({ patientId: id, page, limit: 10 }));
      const latestSession = data?.payload?.data?.[0];
      if (latestSession?.id) {
        const sessionData = await dispatch(getCustomRomSessionById({ customRomId: latestSession.id, page: 1 })) as { payload: { data: ICustomRomSession[] } };
        if (sessionData?.payload?.data?.length > 0) {
          setCustomRomSessionExercise(sessionData.payload.data);
          dispatch(setSelectedRom(sessionData.payload.data[0]));
        } else {
          setCustomRomSessionExercise([]);
        }
      } else {
        setCustomRomSessionExercise([]);
      }
    } catch (error) {
      console.error("Error fetching data", error);
      setCustomRomSessionExercise([]);
    } finally {
      setRomDataLoading(false);
    }
  };

  useEffect(() => {
    if (isPlanOne && customRomSessionExercise?.length > 0) {
      dispatch(setSelectedRom(customRomSessionExercise[0]));
    }
  }, [isPlanOne, dispatch]);

  useEffect(() => {
    initializePlans();
  }, []);

  const initializePlans = async () => {
    const id = user.isPhysioterapist ? selectedUser?.id : user?.id;
    await dispatch(getPlansByUserId(id));
  };

  const programDetails = async (
    current: number,
    functionalGoalsIds: number[],
  ) => {
    await dispatch(getSuggestedProgram({ current, functionalGoalsIds }));
    setLoading(false);
  };

  useEffect(() => {
    dispatch(setActiveTab(ACTIVETAB.HOME));
    (user.isPhysioterapist ? selectedUser?.id : user?.id) && isExistingPatientThere()
  }, [selectedUser])

  const isExistingPatientThere = async () => {
    setLoadingExist(true)
    const romData = await dispatch(getRomSessionList({ patientId: user.isPhysioterapist ? selectedUser?.id : user?.id, page: 1, limit: 10 }))
    const postureData = await dispatch(getPostureByPage({ userId: user.isPhysioterapist ? selectedUser?.id : user?.id, page: 1 }))
    dispatch(setExistingPatientData(romData?.payload?.data?.length > 0 && postureData?.payload?.data?.length > 0))
    setLoadingExist(false)
  }

  useEffect(() => {
    if (suggestedProgramDetails && total !== suggestedProgram.length) {
      suggestedProgramDetails?.meta?.pagination?.page === 1 && setSuggestedProgramData(suggestedProgramDetails?.data?.slice(0, 3));
      if (isFunctionGoalChanged) {
        setSuggestedProgram(suggestedProgramDetails?.data.map(item => ({
          ...item,
          thumbnail: item.thumbnail || { url: '' },
        })));
      } else {
        setSuggestedProgram((prev: ISuggestedProgramItem[]) => [
          ...prev,
          ...(suggestedProgramDetails?.data.map(item => ({
            ...item,
            thumbnail: item.thumbnail || { url: '' },
          })) || []),
        ])
      }
      setTotal(suggestedProgramDetails.meta.pagination.total)
    }
  }, [suggestedProgramDetails, isFunctionGoalChanged, selectedUser])

  useEffect(() => {
    if (savedFunctionalGoals?.functionalGoalsIds?.length > 0) {
      if (!areArraysEqual(savedFunctionalGoals?.functionalGoalsIds, functionalGoal)) {
        setIsFunctionGoalChanged(true)
        setFunctionalGoal(savedFunctionalGoals.functionalGoalsIds)
      } else {
        setIsFunctionGoalChanged(false)
      }
    }
  }, [savedFunctionalGoals, selectedUser])

  const areArraysEqual = (arr1: number[], arr2: number[]): boolean => {
    if (arr1.length !== arr2.length) return false;
    return arr1.every((value, index) => value === arr2[index]);
  };

  useEffect(() => {
    if (current === 0) {
      if (user.isPhysioterapist ? selectedUser?.functionalGoals?.length > 0 : user?.functionalGoals?.length > 0) {
        const lastIndex = user.isPhysioterapist ? selectedUser?.functionalGoals?.length - 1 : user?.functionalGoals?.length - 1;
        const functionGoalIds = user.isPhysioterapist ? selectedUser?.functionalGoals[lastIndex]?.functionalGoalsIds : user?.functionalGoals[lastIndex]?.functionalGoalsIds
        if (total !== suggestedProgram.length) {
          programDetails(currentPage, functionGoalIds)
        }
      } else {
        programDetails(currentPage, [])
      }
    }
  }, [currentPage, selectedUser])

  const fetchHomeData = async (pageNumber: number) => {
    const payload = {
      userId: user.isPhysioterapist ? selectedUser?.id : user?.id,
      limit: 10,
      page: pageNumber,
    };
    const data = await dispatch(getProgramList(payload)) as { payload: { data: Program[]; pagination: IPaginationPluginDefaultResponse } };
    if (data?.payload?.data?.length) {
      if (pageNumber === 1) {
        setProgramData(data?.payload?.data);
      } else {
        setProgramData((prev) => {
          const newData = data?.payload?.data?.filter(
            (item) => !prev?.find((prevItem) => prevItem?.id === item?.id)
          );
          return [...prev, ...newData];
        });
      }
    }
    if (data?.payload?.data?.length >= 1) {
      dispatch(setCurrentStep(INTAKE_STEPS.SUGGESTED))
    } else {
      dispatch(setCurrentStep(INTAKE_STEPS.INTAKE_PROGRAMS))
    }
    setPagination(data?.payload?.pagination);
  };

  const renderScreeningPlan = () => {
    if (isRomDataLoading) {
      return <Spin className="spin-css" tip="Loading" size="large" />;
    }
    return (
      <>
        {customRomSessionExercise?.length > 0 ? (
          <AiAssistantCustomRomScanResult />
        ) : (
          <div className={`select-none`} style={{ backgroundColor: 'var(--home-bg-color)', paddingTop: '20px', paddingBottom: '10px' }}>
            <div className='m-5'>
              <PatientDashboardBanner />
            </div>
          </div>
        )}
      </>
    );
  }

  const renderVirtualPtPlan = () => {
    return (
      <div style={{ backgroundColor: 'var(--home-bg-color)'}} className='mt-32 p-5'>
        <div className='pt-5'>
        <MyProgramsPatientOnboard programData={programData} fetchHomeData={fetchHomeData} pagination={{ pageCount: pagination?.pageCount!, totalCount: total }} />
      </div>
      </div>
    )
  }

  const renderDefaultPlan = () => {
    const showBanner = !existingPatientData || (user.isPhysioterapist ? !selectedUser?.profile?.isWellnessCheck : !user?.profile?.isWellnessCheck);
    return (
      <div className={`select-none`} style={{ backgroundColor: 'var(--home-bg-color)', paddingTop: (!existingPatientData || (user.isPhysioterapist ? !selectedUser?.profile?.isWellnessCheck : !user?.profile?.isWellnessCheck)) ? '20px' : '0px', paddingBottom: '10px' }}>
        <>
          {showBanner ?
            <div className='m-5'><PatientDashboardBanner /></div> :
            <ExistingPatientDashboard suggestedProgramData={suggestedProgramData} fetchHomeData={fetchHomeData} />}
          <div className='m-5'>
            <MyProgramsPatientOnboard programData={programData} fetchHomeData={fetchHomeData} pagination={{ pageCount: pagination?.pageCount!, totalCount: total }} />
            {(user.isPhysioterapist || user?.profile?.isWellnessCheck) && <>
              <SuggestedPrograms current={current} setCurrent={setCurrent} suggestedProgramData={suggestedProgram} currentPage={currentPage} setCurrentPage={setCurrentPage} fetchHomeData={fetchHomeData} />
              <RecommendationDashboard />
              <TrueToFormPatientOnboard /></>}
          </div>
        </>
      </div>
    )
  }

  if (isLoading || isLoadingExist) {
    return <Spin className="spin-css" tip="Loading" size="large" />;
  }
  if (isPlanOne && !isVirtualPt) {
    return renderScreeningPlan();
  }
  if (isVirtualPt) {
    return renderVirtualPtPlan();
  }
  return renderDefaultPlan();
}
